import {
  _ELLIPSIS_CHARACTER as ELLIPSIS_CHARACTER,
  type TruncationMode,
} from '@dynatrace/strato-components/typography';

import { checkTextWidth } from '../components/canvas/utils/measure-text-width.js';
import { DEFAULT_TRUNCATION_MODE } from '../constants.js';

function buildEllipsizedText(
  originalText: string,
  prefixSize: number,
  suffixSize: number,
) {
  return `${originalText.substring(
    0,
    prefixSize,
  )}${ELLIPSIS_CHARACTER}${originalText.substring(
    originalText.length - suffixSize,
  )}`;
}

const defineInitialTextAffixesLength = (
  ellipsis: 'start' | 'middle' | 'end',
  estimatedCharsFitting: number,
) => {
  let prefixSize: number;
  let suffixSize: number;
  switch (ellipsis) {
    case 'start': {
      prefixSize = 0;
      suffixSize = estimatedCharsFitting;
      break;
    }
    case 'end': {
      prefixSize = estimatedCharsFitting;
      suffixSize = 0;
      break;
    }
    default: {
      const halfOfTheChars = estimatedCharsFitting / 2;
      prefixSize =
        estimatedCharsFitting % 2 === 0 ? halfOfTheChars : halfOfTheChars + 1;
      suffixSize = halfOfTheChars;
    }
  }
  return [prefixSize, suffixSize];
};

export const processTextOverflow = (
  originalText: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  ellipsis: TruncationMode = DEFAULT_TRUNCATION_MODE,
) => {
  const originalTextWidth = checkTextWidth(originalText, ctx);
  if (originalTextWidth < maxWidth) {
    return originalText;
  }

  const averageCharWidth = originalTextWidth / originalText.length;
  const ellipsisWidth = checkTextWidth(ELLIPSIS_CHARACTER, ctx);
  const widthRemaining = maxWidth - ellipsisWidth;
  const estimatedCharsFitting = widthRemaining / averageCharWidth;
  let resultingText;
  let resultingTextWidth;

  let [prefixSize, suffixSize] = defineInitialTextAffixesLength(
    ellipsis,
    estimatedCharsFitting,
  );

  const defineAffixesReduction = (ellipsis: TruncationMode) => {
    switch (ellipsis) {
      case 'start': {
        suffixSize -= 1;
        break;
      }
      case 'end': {
        prefixSize -= 1;
        break;
      }
      default: {
        if (prefixSize > suffixSize) {
          prefixSize--;
        } else {
          suffixSize--;
        }
      }
    }
    return [prefixSize, suffixSize];
  };

  resultingText = buildEllipsizedText(originalText, prefixSize, suffixSize);
  resultingTextWidth = checkTextWidth(resultingText, ctx);

  while (resultingTextWidth >= maxWidth && prefixSize > 0 && suffixSize > 0) {
    [prefixSize, suffixSize] = defineAffixesReduction(ellipsis);

    resultingText = buildEllipsizedText(originalText, prefixSize, suffixSize);
    resultingTextWidth = checkTextWidth(resultingText, ctx);
  }

  return resultingText;
};
